/* ─────────────────────────────────────────────────────────────────────────
 * Arcade kit: the small pieces the inner pages share with the home cabinet.
 *
 * They render the cabinet's own classes (.cab-head, .cab-icon, .cab-portrait)
 * next to the .arc block at the end of globals.css, and read the same
 * --cab-* colours. Layout sets those from PAL through ARCADE_VARS, so a
 * colour still changes in PAL only.
 * ───────────────────────────────────────────────────────────────────────── */

import type { CSSProperties, FC, ReactNode } from 'react'
import type { IconName } from '@/components/landing/cabinet/data'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { ICONS, PAL, RESUME } from '@/components/landing/cabinet/data'
import { paintPortraitPhoto, paintPortraitSprite } from '@/components/landing/cabinet/pixel'

/* the palette, as the variables every .cab and .arc rule reads */
export const ARCADE_VARS = {
    '--cab-cream': PAL.cream,
    '--cab-cream-hi': PAL.creamHi,
    '--cab-plum': PAL.plum,
    '--cab-peach': PAL.peach,
    '--cab-tan': PAL.tan,
    '--cab-sage': PAL.sage,
    '--cab-teal': PAL.teal,
    '--cab-rust': PAL.rust
} as CSSProperties

/* 11×11 bitmaps in the cabinet's format, "k" is an inked cell */
const EXTRA_ICONS = {
    trophy: [
        'kkkkkkkkkkk',
        'k.kkkkkkk.k',
        'k.kkkkkkk.k',
        '.kkkkkkkkk.',
        '..kkkkkkk..',
        '...kkkkk...',
        '....kkk....',
        '.....k.....',
        '....kkk....',
        '..kkkkkkk..',
        '..kkkkkkk..'
    ],
    heart: [
        '...........',
        '.kkk...kkk.',
        'kkkkk.kkkkk',
        'kkkkkkkkkkk',
        'kkkkkkkkkkk',
        '.kkkkkkkkk.',
        '..kkkkkkk..',
        '...kkkkk...',
        '....kkk....',
        '.....k.....',
        '...........'
    ],
    gamepad: [
        '...........',
        '...........',
        '.kkkkkkkkk.',
        'kk.kkkk.kkk',
        'k...kk.k.kk',
        'kk.kkkk.kkk',
        'kkkkkkkkkkk',
        'kkk.....kkk',
        '.kk.....kk.',
        '...........',
        '...........'
    ],
    clock: [
        '...kkkkk...',
        '.kk.....kk.',
        '.k...k...k.',
        'k....k....k',
        'k....k....k',
        'k....kkk..k',
        'k.........k',
        'k.........k',
        '.k.......k.',
        '.kk.....kk.',
        '...kkkkk...'
    ],
    chat: [
        '...........',
        'kkkkkkkkkkk',
        'k.........k',
        'k.kkkkkkk.k',
        'k.........k',
        'k.kkkkk...k',
        'k.........k',
        'kkkkkkkkkkk',
        '.kk........',
        '.k.........',
        '...........'
    ],
    person: [
        '....kkk....',
        '...kkkkk...',
        '...kkkkk...',
        '...kkkkk...',
        '....kkk....',
        '...........',
        '..kkkkkkk..',
        '.kkkkkkkkk.',
        '.kkkkkkkkk.',
        '.kkkkkkkkk.',
        '...........'
    ],
    book: [
        '...........',
        '.kkkk.kkkk.',
        'k....k....k',
        'k.kk.k.kk.k',
        'k....k....k',
        'k.kk.k.kk.k',
        'k....k....k',
        'k.kk.k.kk.k',
        'k....k....k',
        'kkkkkkkkkkk',
        '...........'
    ],
    down: [
        '....kkk....',
        '....kkk....',
        '....kkk....',
        '....kkk....',
        'kkkkkkkkkkk',
        '.kkkkkkkkk.',
        '..kkkkkkk..',
        '...kkkkk...',
        '....kkk....',
        '.....k.....',
        '...........'
    ],
    right: [
        '...........',
        '...k.......',
        '...kk......',
        '...kkk.....',
        '...kkkk....',
        '...kkkkk...',
        '...kkkk....',
        '...kkk.....',
        '...kk......',
        '...k.......',
        '...........'
    ],
    check: [
        '...........',
        '..........k',
        '.........kk',
        '........kk.',
        'k......kk..',
        'kk....kk...',
        '.kk..kk....',
        '..kkkk.....',
        '...kk......',
        '...........',
        '...........'
    ]
} satisfies Record<string, string[]>

export type ArcadeIconName = IconName | keyof typeof EXTRA_ICONS

export type Tone = 'sage' | 'tan' | 'peach'

const ARCADE_ICONS: Record<ArcadeIconName, string[]> = { ...ICONS, ...EXTRA_ICONS }

/* each row's inked runs as one path, so an icon is a single element */
const paths = new Map<ArcadeIconName, string>()
const iconPath = (name: ArcadeIconName): string => {
    const cached = paths.get(name)
    if (cached) return cached
    let d = ''
    ARCADE_ICONS[name].forEach((row, y) => {
        let x = 0
        while (x < row.length) {
            if (row[x] !== 'k') {
                x++
                continue
            }
            let end = x
            while (end < row.length && row[end] === 'k') end++
            d += `M${x} ${y}h${end - x}v1h${x - end}z`
            x = end
        }
    })
    paths.set(name, d)
    return d
}

/* ── Shared class strings ────────────────────────────────────────────────
 * Every inner-page piece is styled with utilities here, so it never depends
 * on a stylesheet block. Colours come from the --cab-* variables Layout sets. */

const PIXEL = 'font-[family-name:var(--font-silkscreen,monospace)] uppercase'

const OUTLINE_SM =
    '[text-shadow:2px_0_0_var(--cab-plum),-2px_0_0_var(--cab-plum),0_2px_0_var(--cab-plum),0_-2px_0_var(--cab-plum),3px_3px_0_var(--cab-plum)]'

const OUTLINE_LG =
    '[text-shadow:2px_0_0_var(--cab-plum),-2px_0_0_var(--cab-plum),0_2px_0_var(--cab-plum),0_-2px_0_var(--cab-plum),2px_2px_0_var(--cab-plum),-2px_-2px_0_var(--cab-plum),2px_-2px_0_var(--cab-plum),-2px_2px_0_var(--cab-plum),4px_4px_0_var(--cab-plum)]'

const RING = 'shadow-[inset_0_0_0_3px_var(--cab-plum)]'

export const ARC = {
    pixel: PIXEL,
    page: 'bg-[radial-gradient(110%_55%_at_50%_0%,#3e5362_0%,#2f414e_100%)] pb-24 pt-24 md:pb-32 md:pt-28',
    wrap: 'mx-auto max-w-[1240px] px-5 md:px-12',
    section: 'mt-20 scroll-mt-24 outline-none md:mt-28',
    text: 'm-0 max-w-[62ch] text-base leading-relaxed text-(--cab-plum) md:text-[17px]',
    outline: cn(PIXEL, 'font-bold text-(--cab-teal)', OUTLINE_LG),
    h3: cn(PIXEL, 'm-0 text-base font-bold leading-snug tracking-[0.03em] text-(--cab-teal) md:text-xl', OUTLINE_SM),
    label: cn(PIXEL, 'text-xs tracking-[0.08em]'),
    btn: cn(
        PIXEL,
        'inline-flex min-h-12 cursor-pointer items-center justify-center gap-3 border-0 bg-(--cab-cream-hi) px-5 py-3.5 text-[13px] leading-none tracking-[0.06em] text-(--cab-plum) no-underline',
        'shadow-[0_0_0_3px_var(--cab-plum),4px_4px_0_3px_var(--cab-plum)] transition-transform hover:bg-(--cab-tan)',
        'active:translate-x-[3px] active:translate-y-[3px] active:shadow-[0_0_0_3px_var(--cab-plum),1px_1px_0_3px_var(--cab-plum)]',
        'focus-visible:outline-3 focus-visible:outline-offset-[7px] focus-visible:outline-dashed focus-visible:outline-(--cab-plum)',
        'disabled:cursor-progress disabled:bg-(--cab-cream-hi)'
    ),
    btnPrimary: 'bg-(--cab-tan) hover:bg-(--cab-teal) hover:text-(--cab-cream-hi)',
    glyph: 'size-[13px] flex-none fill-current'
}

/* the stepped pixel corner, `s` px per step */
const stepped = (s: number): string => {
    const d = s * 2
    return `polygon(0 ${d}px, ${s}px ${d}px, ${s}px ${s}px, ${d}px ${s}px, ${d}px 0, calc(100% - ${d}px) 0, calc(100% - ${d}px) ${s}px, calc(100% - ${s}px) ${s}px, calc(100% - ${s}px) ${d}px, 100% ${d}px, 100% calc(100% - ${d}px), calc(100% - ${s}px) calc(100% - ${d}px), calc(100% - ${s}px) calc(100% - ${s}px), calc(100% - ${d}px) calc(100% - ${s}px), calc(100% - ${d}px) 100%, ${d}px 100%, ${d}px calc(100% - ${s}px), ${s}px calc(100% - ${s}px), ${s}px calc(100% - ${d}px), 0 calc(100% - ${d}px))`
}

const TONE_BG: Record<Tone, string> = {
    sage: 'bg-(--cab-sage)',
    tan: 'bg-(--cab-tan)',
    peach: 'bg-(--cab-peach)'
}

/* ── Pieces ───────────────────────────────────────────────────────────── */

interface PixelIconProps {
    name: ArcadeIconName
    className?: string
}

export const PixelIcon: FC<PixelIconProps> = ({ name, className = 'size-6 fill-(--cab-plum)' }): ReactNode => {
    return (
        <svg viewBox='0 0 11 11' className={className} shapeRendering='crispEdges' aria-hidden focusable='false'>
            <path d={iconPath(name)} />
        </svg>
    )
}

interface IconTileProps {
    name: ArcadeIconName
    tone?: Tone
}

/* an icon in a ringed square, the same tile the section heads use */
export const IconTile: FC<IconTileProps> = ({ name, tone = 'sage' }): ReactNode => {
    return (
        <span className={cn('grid size-11 flex-none place-items-center md:size-12', RING, TONE_BG[tone])} aria-hidden>
            <PixelIcon name={name} />
        </span>
    )
}

interface SectionHeadProps {
    icon: ArcadeIconName
    label: string
    level?: 2 | 3
    id?: string
}

/* the résumé window's section head, as a real heading */
export const SectionHead: FC<SectionHeadProps> = ({ icon, label, level = 3, id }): ReactNode => {
    const Tag = level === 2 ? 'h2' : 'h3'
    return (
        <Tag id={id} className='m-0 flex items-stretch'>
            <span className={cn('grid size-11 flex-none place-items-center bg-(--cab-sage) md:size-12', RING)} aria-hidden>
                <PixelIcon name={icon} />
            </span>
            <span className='my-[7px] flex max-w-[380px] flex-1 items-center px-3.5 shadow-[inset_0_3px_0_var(--cab-plum),inset_0_-3px_0_var(--cab-plum),inset_-3px_0_0_var(--cab-plum)]'>
                <span className={cn(PIXEL, 'text-[15px] font-bold tracking-[0.04em] text-(--cab-teal) md:text-lg', OUTLINE_SM)}>
                    {label}
                </span>
            </span>
        </Tag>
    )
}

interface StageTitleProps {
    stage: string
    title: string
    id: string
    intro?: string
}

/* a section title set on the slate cabinet body, outside any window */
export const StageTitle: FC<StageTitleProps> = ({ stage, title, id, intro }): ReactNode => {
    return (
        <div className='mb-8 flex max-w-[760px] flex-col gap-3.5 md:mb-12'>
            <p className={cn(PIXEL, 'm-0 flex items-center gap-3 text-xs tracking-[0.14em] text-(--cab-tan)')}>
                <span className='size-3 flex-none bg-(--cab-peach) shadow-[3px_3px_0_#1d272e]' aria-hidden />
                {stage}
            </p>
            <h2
                id={id}
                className={cn(
                    PIXEL,
                    'm-0 text-[28px] font-bold leading-[1.08] tracking-[0.02em] text-(--cab-cream-hi) text-balance sm:text-4xl lg:text-5xl',
                    '[text-shadow:0_3px_0_var(--cab-teal),0_6px_0_#1d272e]'
                )}
            >
                {title}
            </h2>
            {intro && <p className='m-0 max-w-[60ch] text-base leading-relaxed text-[#c9c2ad] md:text-lg'>{intro}</p>}
        </div>
    )
}

interface PixelPanelProps {
    children: ReactNode
    size?: 'md' | 'sm'
    className?: string
}

/* the dialog window: plum frame, stepped corners, cream face, peach shadow */
export const PixelPanel: FC<PixelPanelProps> = ({ children, size = 'md', className }): ReactNode => {
    const sm = size === 'sm'
    return (
        <div
            className={cn(
                sm
                    ? '[filter:drop-shadow(6px_6px_0_var(--cab-peach))_drop-shadow(3px_3px_0_var(--cab-plum))]'
                    : '[filter:drop-shadow(8px_8px_0_var(--cab-peach))_drop-shadow(4px_4px_0_var(--cab-plum))]',
                className
            )}
        >
            <div className={cn('h-full bg-(--cab-plum)', sm ? 'p-1' : 'p-1.5')} style={{ clipPath: stepped(sm ? 4 : 6) }}>
                <div className='h-full bg-(--cab-cream) text-(--cab-plum)' style={{ clipPath: stepped(sm ? 3 : 4) }}>
                    {children}
                </div>
            </div>
        </div>
    )
}

/* the cabinet's pixel portrait: the drawn bust, or RESUME.portraitSrc
 * repainted in the palette */
export const PixelPortrait: FC = (): ReactNode => {
    const ref = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const c = ref.current
        if (!c) return
        paintPortraitSprite(c)
        if (!RESUME.portraitSrc) return
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => paintPortraitPhoto(c, img)
        img.src = RESUME.portraitSrc
    }, [])

    return (
        <div className='aspect-square w-full max-w-[240px] self-center bg-(--cab-plum) p-1.5'>
            <canvas
                ref={ref}
                role='img'
                aria-label={`Pixel portrait of ${RESUME.name}`}
                className='block size-full [image-rendering:pixelated]'
            />
        </div>
    )
}
